import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, Overline, StatusPill } from "@/components/common";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Phone, Mail, User, Car, Plus, Check, X, Pencil, ChevronRight, Sparkle,
  UserX, MoveRight,
} from "lucide-react";

const DAY_FILTERS = [{ v: "today", l: "Today" }, { v: "tomorrow", l: "Tomorrow" }];
const PERIODS = [{ v: "all", l: "All Day" }, { v: "morning", l: "Morning" }, { v: "afternoon", l: "Afternoon" }];

export default function TeeSheet() {
  const [day, setDay] = useState("today");
  const [period, setPeriod] = useState("all");
  const [availOnly, setAvailOnly] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addName, setAddName] = useState("");
  const [resName, setResName] = useState("");
  const [resPlayers, setResPlayers] = useState(2);
  const [resCart, setResCart] = useState(true);
  const [notes, setNotes] = useState("");
  const [moving, setMoving] = useState(false);

  const load = useCallback(() => {
    api.get(`/teetimes?day=${day}&period=${period}&available=${availOnly}`).then(({ data }) => setData(data));
  }, [day, period, availOnly]);
  useEffect(() => { load(); }, [load]);

  const open = async (row) => {
    setSel(row); setEditing(false); setAddName(""); setResName(""); setResPlayers(2); setResCart(true); setMoving(false);
    const { data } = await api.get(`/teetimes/${row.id}`);
    setDetail(data); setNotes(data.teetime.notes || "");
  };
  const refresh = async () => {
    load();
    if (sel) { const { data } = await api.get(`/teetimes/${sel.id}`); setDetail(data); setSel(data.teetime); setNotes(data.teetime.notes || ""); }
  };

  const checkIn = async () => { await api.post(`/teetimes/${sel.id}/checkin`); toast.success("Golfer checked in", { description: sel.primary_name }); await refresh(); };
  const cancel = async () => { await api.post(`/teetimes/${sel.id}/cancel`); toast.success("Reservation cancelled"); await refresh(); };
  const noShow = async () => { await api.post(`/teetimes/${sel.id}/no-show`); toast.success("Marked as no-show", { description: sel.primary_name }); await refresh(); };
  const saveNotes = async () => { await api.put(`/teetimes/${sel.id}/notes`, { notes }); toast.success("Notes saved"); };
  const addPlayer = async () => {
    if (!addName.trim()) return;
    try { await api.post(`/teetimes/${sel.id}/add-player`, { name: addName }); toast.success("Player added", { description: addName }); setAddName(""); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add player"); }
  };
  const reserve = async () => {
    if (!resName.trim()) return;
    try { await api.post(`/teetimes/${sel.id}/reserve`, { name: resName, players: resPlayers, cart: resCart, source: "Phone" }); toast.success("Reservation created", { description: `${resName} · ${resPlayers} players` }); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reserve"); }
  };
  const saveEdit = async (updates) => { await api.put(`/teetimes/${sel.id}`, updates); toast.success("Reservation updated"); setEditing(false); await refresh(); };
  const moveTo = async (targetId) => {
    try { const { data } = await api.post(`/teetimes/${sel.id}/move`, { target_id: targetId }); toast.success("Tee time moved", { description: `Now at ${data.moved_to}` }); setMoving(false); setSel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not move"); }
  };

  const availableSlots = (data?.rows || []).filter((r) => r.status === "Available" && r.id !== sel?.id);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Tee Sheet</h1>
          <p className="text-[#52525B] mt-1">Manage today's play and reservations.</p>
        </div>
        {data && (
          <div className="text-right">
            <div className="font-display text-3xl font-semibold text-[#0F3F2D] font-mono2">{data.booked_pct}%</div>
            <Overline>Booked · {data.booked}/{data.total}</Overline>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Seg options={DAY_FILTERS} value={day} onChange={setDay} testid="filter-day" />
        <Seg options={PERIODS} value={period} onChange={setPeriod} testid="filter-period" />
        <button data-testid="filter-available" onClick={() => setAvailOnly((v) => !v)}
          className={cn("px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors",
            availOnly ? "bg-[#0F3F2D] text-white border-transparent" : "bg-white text-[#52525B] border-[#E5E7EB] hover:border-[#0F3F2D]/30")}>
          Available Only
        </button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left">
                {["Tee Time", "Golfers", "Players", "Green Fee", "Cart", "Payment", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-3 overline font-semibold text-[#71717A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((r) => (
                <tr key={r.id} data-testid={`teetime-row-${r.sort}`} onClick={() => open(r)}
                  className="border-b border-[#F1F3F2] last:border-0 hover:bg-[#0F3F2D]/[0.02] cursor-pointer transition-colors group">
                  <td className="px-5 py-3 font-mono2 font-medium text-[#1C1F1E] whitespace-nowrap">{r.time}</td>
                  <td className="px-5 py-3 text-[#1C1F1E] max-w-[260px]">
                    {r.players ? (
                      <span className="truncate block">{r.golfers[0]}{r.players > 1 && <span className="text-[#A1A1AA]"> +{r.players - 1}</span>}</span>
                    ) : r.intel ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-[#0F3F2D]"><Sparkle size={11} className="text-[#C7A24A]" />{r.intel.tag}</span>
                    ) : <span className="text-[#C1C4C6]">—</span>}
                  </td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">{r.players || "—"}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">${r.green_fee}</td>
                  <td className="px-5 py-3">{r.status !== "Available" && r.cart ? <Car size={15} className="text-[#0F3F2D]" /> : <span className="text-[#C1C4C6]">—</span>}</td>
                  <td className="px-5 py-3">{r.payment_status ? <StatusPill status={r.payment_status} /> : <span className="text-[#C1C4C6]">—</span>}</td>
                  <td className="px-5 py-3"><StatusPill status={r.status} /></td>
                  <td className="px-5 py-3"><ChevronRight size={15} className="text-[#C1C4C6] group-hover:text-[#0F3F2D] transition-colors" /></td>
                </tr>
              ))}
              {data?.rows.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-[#A1A1AA]">No tee times match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto bg-white/95 backdrop-blur-2xl" data-testid="teetime-panel">
          <SheetTitle className="sr-only">Tee Time Details</SheetTitle>
          {detail && (
            <div>
              <div className="px-6 py-5 border-b border-[#E5E7EB] sidebar-grain text-white">
                <Overline className="!text-white/50">{day === "today" ? "Today" : "Tomorrow"} · {detail.teetime.source || "Open"}</Overline>
                <div className="flex items-center justify-between mt-1">
                  <div className="font-display text-3xl font-semibold font-mono2">{detail.teetime.time}</div>
                  <StatusPill status={detail.teetime.status} className="!bg-white/10 !text-white !border-white/20" />
                </div>
              </div>

              {detail.teetime.status === "Available" ? (
                <div className="p-6 space-y-5">
                  {detail.teetime.intel && (
                    <div className="rounded-xl border border-[#0F3F2D]/15 bg-[#0F3F2D]/[0.04] p-4">
                      <div className="flex items-center gap-1.5 mb-1"><Sparkle size={13} className="text-[#C7A24A]" /><Overline className="!text-[#0F3F2D]">Scratch Signal</Overline></div>
                      <p className="text-sm text-[#1C1F1E]">{detail.teetime.intel.tag}. Suggested offer <span className="font-mono2 font-semibold">${detail.teetime.intel.offer}</span> · ~{detail.teetime.intel.likely} likely golfers.</p>
                    </div>
                  )}
                  <div>
                    <Overline>Create Reservation</Overline>
                    <div className="mt-3 space-y-3">
                      <input value={resName} onChange={(e) => setResName(e.target.value)} placeholder="Golfer name" data-testid="reserve-name"
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0F3F2D]/40" />
                      <div className="flex items-center justify-between text-sm"><span className="text-[#52525B]">Players</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setResPlayers((p) => Math.max(1, p - 1))} className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white">−</button>
                          <span className="w-6 text-center font-mono2">{resPlayers}</span>
                          <button onClick={() => setResPlayers((p) => Math.min(4, p + 1))} className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white">+</button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm"><span className="text-[#52525B]">Cart</span>
                        <button onClick={() => setResCart((c) => !c)} className={cn("px-3 py-1 rounded-md text-xs font-medium border", resCart ? "bg-[#0F3F2D] text-white border-transparent" : "bg-white border-[#E5E7EB] text-[#52525B]")}>{resCart ? "Included" : "Walking"}</button>
                      </div>
                      <Button data-testid="reserve-btn" onClick={reserve} className="w-full bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg h-10"><Plus size={15} className="mr-1.5" />Create Reservation</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div>
                    <Overline>Primary Golfer</Overline>
                    <div className="mt-2 font-semibold text-lg text-[#1C1F1E]">{detail.teetime.primary_name}</div>
                    <div className="mt-2 space-y-1.5 text-sm text-[#52525B]">
                      <div className="flex items-center gap-2"><Phone size={14} className="text-[#A1A1AA]" />{detail.teetime.phone}</div>
                      <div className="flex items-center gap-2"><Mail size={14} className="text-[#A1A1AA]" />{detail.teetime.email}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Stat label="Players" value={detail.teetime.players} />
                    <Stat label="Green Fee" value={`$${detail.teetime.green_fee}`} />
                    <Stat label="Cart" value={detail.teetime.cart ? "Included" : "Walking"} />
                    <Stat label="Source" value={detail.teetime.source} />
                    <div className="rounded-lg border border-[#E5E7EB] p-3 col-span-2">
                      <div className="text-xs text-[#A1A1AA]">Payment Status</div>
                      <div className="mt-1"><StatusPill status={detail.teetime.payment_status || "Unpaid"} /></div>
                    </div>
                  </div>

                  <div>
                    <Overline>Group</Overline>
                    <div className="mt-2 space-y-1.5">
                      {detail.teetime.golfers.map((g, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-sm text-[#1C1F1E]">
                          <span className="w-6 h-6 rounded-full bg-[#0F3F2D]/8 text-[#0F3F2D] text-xs flex items-center justify-center font-mono2">{i + 1}</span>{g}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Add player..." className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#0F3F2D]/40" />
                      <button data-testid="add-player-inline" onClick={addPlayer} className="px-3 py-1.5 rounded-lg border border-[#E5E7EB] hover:border-[#0F3F2D]/40 text-sm text-[#0F3F2D] font-medium flex items-center gap-1"><Plus size={14} />Add</button>
                    </div>
                  </div>

                  {detail.golfer && (
                    <div className="rounded-xl border border-[#E5E7EB] bg-[#F7F9F8] p-4">
                      <Overline>Customer History</Overline>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div><div className="text-xs text-[#A1A1AA]">Rounds YTD</div><div className="font-mono2 font-semibold text-[#1C1F1E]">{detail.golfer.rounds_ytd}</div></div>
                        <div><div className="text-xs text-[#A1A1AA]">Lifetime Spend</div><div className="font-mono2 font-semibold text-[#0F3F2D]">${detail.golfer.lifetime_spend.toLocaleString()}</div></div>
                        <div><div className="text-xs text-[#A1A1AA]">Segment</div><div className="font-medium text-[#1C1F1E]">{detail.golfer.segment}</div></div>
                        <div><div className="text-xs text-[#A1A1AA]">No-Shows</div><div className="font-mono2 text-[#1C1F1E]">{detail.golfer.no_shows}</div></div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Overline>Notes</Overline>
                    <textarea data-testid="teetime-notes" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} rows={2}
                      placeholder="Add a note for staff..." className="w-full mt-2 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0F3F2D]/40 resize-none" />
                  </div>

                  {moving ? (
                    <div className="rounded-xl border border-[#0F3F2D]/20 bg-[#0F3F2D]/[0.03] p-4">
                      <Overline>Move to available tee time</Overline>
                      <div className="mt-2 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                        {availableSlots.map((s) => (
                          <button key={s.id} data-testid="move-option" onClick={() => moveTo(s.id)} className="px-2 py-2 rounded-lg border border-[#E5E7EB] bg-white text-sm font-mono2 hover:border-[#0F3F2D]/40">{s.time}</button>
                        ))}
                        {availableSlots.length === 0 && <div className="col-span-3 text-sm text-[#A1A1AA] py-4 text-center">No open slots.</div>}
                      </div>
                      <button onClick={() => setMoving(false)} className="mt-2 text-xs text-[#71717A] hover:text-[#1C1F1E]">Cancel move</button>
                    </div>
                  ) : editing ? (
                    <EditForm teetime={detail.teetime} onSave={saveEdit} onCancel={() => setEditing(false)} />
                  ) : (
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      {["Reserved"].includes(detail.teetime.status) && (
                        <Button data-testid="checkin-btn" onClick={checkIn} className="bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg col-span-2"><Check size={15} className="mr-1.5" />Check In</Button>
                      )}
                      <Button data-testid="edit-btn" variant="outline" onClick={() => setEditing(true)} className="rounded-lg border-[#E5E7EB]"><Pencil size={14} className="mr-1.5" />Edit</Button>
                      <Button data-testid="move-btn" variant="outline" onClick={() => setMoving(true)} className="rounded-lg border-[#E5E7EB]"><MoveRight size={14} className="mr-1.5" />Move</Button>
                      <Button data-testid="no-show-btn" variant="outline" onClick={noShow} className="rounded-lg border-[#D97706]/30 text-[#D97706] hover:bg-[#D97706]/5"><UserX size={14} className="mr-1.5" />No-Show</Button>
                      <Button data-testid="cancel-reservation-btn" variant="outline" onClick={cancel} className="rounded-lg border-[#DC2626]/30 text-[#DC2626] hover:bg-[#DC2626]/5"><X size={14} className="mr-1.5" />Cancel</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex bg-white border border-[#E5E7EB] rounded-lg p-0.5" data-testid={testid}>
    {options.map((o) => (
      <button key={o.v} onClick={() => onChange(o.v)}
        className={cn("px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors",
          value === o.v ? "bg-[#0F3F2D] text-white" : "text-[#52525B] hover:text-[#1C1F1E]")}>
        {o.l}
      </button>
    ))}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-[#E5E7EB] p-3">
    <div className="text-xs text-[#A1A1AA]">{label}</div>
    <div className="font-medium text-[#1C1F1E] mt-0.5">{value}</div>
  </div>
);

function EditForm({ teetime, onSave, onCancel }) {
  const [players, setPlayers] = useState(teetime.players);
  const [cart, setCart] = useState(teetime.cart);
  const [fee, setFee] = useState(teetime.green_fee);
  return (
    <div className="rounded-xl border border-[#0F3F2D]/20 bg-[#0F3F2D]/[0.03] p-4 space-y-3">
      <Overline>Edit Reservation</Overline>
      <div className="flex items-center justify-between text-sm"><span className="text-[#52525B]">Players</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setPlayers((p) => Math.max(1, p - 1))} className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white">−</button>
          <span className="w-6 text-center font-mono2">{players}</span>
          <button onClick={() => setPlayers((p) => Math.min(4, p + 1))} className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white">+</button>
        </div>
      </div>
      <div className="flex items-center justify-between text-sm"><span className="text-[#52525B]">Cart</span>
        <button onClick={() => setCart((c) => !c)} className={cn("px-3 py-1 rounded-md text-xs font-medium border", cart ? "bg-[#0F3F2D] text-white border-transparent" : "bg-white border-[#E5E7EB] text-[#52525B]")}>{cart ? "Included" : "Walking"}</button>
      </div>
      <div className="flex items-center justify-between text-sm"><span className="text-[#52525B]">Green Fee</span>
        <input type="number" value={fee} onChange={(e) => setFee(Number(e.target.value))} className="w-24 border border-[#E5E7EB] rounded-md px-2 py-1 text-right font-mono2 outline-none" />
      </div>
      <div className="flex gap-2 pt-1">
        <Button data-testid="save-edit-btn" onClick={() => onSave({ players, cart, green_fee: fee })} className="flex-1 bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg h-9">Save</Button>
        <Button variant="outline" onClick={onCancel} className="rounded-lg h-9 border-[#E5E7EB]">Cancel</Button>
      </div>
    </div>
  );
}
